import { hasDesignStudioUsage, isObjectOrRelationshipAttribute } from './item';
import type { DataIndexItemKind, DataIndexUsage, DataIndexUsageKey } from './types';

/**
 * Which usage surfaces an item has at all.
 *
 * Not how many things are on them: a surface is absent because the plan has
 * no such thing or the workspace does not show it, which is a different
 * answer from a surface with nothing on it. The drawer's accordions and the
 * item page's views each list the surfaces in their own order, and both ask
 * here which ones exist, so the two cannot drift apart.
 */

export type UsageScope = {
  kind: DataIndexItemKind;
  /** Builder plans have no segments, campaigns or layouts. */
  isBuilder: boolean;
  /** Whether the workspace shows content usage for object and relationship attributes. */
  withTemplatesSnippetsLayouts: boolean;
};

/** Whether an item shows content usage at all. */
export function withContentUsage(scope: UsageScope): boolean {
  return isObjectOrRelationshipAttribute(scope.kind) ? scope.withTemplatesSnippetsLayouts : true;
}

function isOffered(id: DataIndexUsageKey, scope: UsageScope): boolean {
  const withContent = withContentUsage(scope);

  switch (id) {
    case 'segments':
    case 'campaigns':
      return !scope.isBuilder;
    case 'newsletters':
      return true;
    case 'templates':
    case 'snippets':
      return withContent;
    case 'design_studio':
      return hasDesignStudioUsage(scope.kind, withContent);
    case 'layouts':
      return withContent && !scope.isBuilder;
    default: {
      const _exhaustive: never = id;
      throw new Error(`unhandled usage surface: ${String(_exhaustive)}`);
    }
  }
}

/** The surfaces this item has, out of the ones a caller lists, in that order. */
export function offeredUsageSurfaces(order: readonly DataIndexUsageKey[], scope: UsageScope): DataIndexUsageKey[] {
  return order.filter((id) => isOffered(id, scope));
}

/**
 * Whether to warn that an object or relationship attribute's content usage
 * is not tracked in Liquid.
 *
 * Only for those two kinds, only where the workspace shows content usage at
 * all, and only once the item is actually on one of the three content
 * surfaces — a warning about templates is noise for an attribute no template
 * mentions. Design Studio is deliberately not one of the three: the page
 * this replaces counts templates, snippets and layouts alone
 * (`data-index-item/index.gts:577-592`).
 */
export function showsLiquidCaveat(usage: DataIndexUsage, scope: UsageScope): boolean {
  if (!isObjectOrRelationshipAttribute(scope.kind) || !withContentUsage(scope)) {
    return false;
  }

  return (usage.templates?.length ?? 0) > 0 || (usage.snippets?.length ?? 0) > 0 || (usage.layouts?.length ?? 0) > 0;
}
